import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, Users, Search, Loader2, X } from "lucide-react";
import UserBadge from "@/components/UserBadge";
import AdminToggle from "@/components/AdminToggle";
import { Input } from "@/components/ui/input";
import { fetchAdminFounders } from "@/lib/api";
import { toast } from "sonner";

/**
 * AdminFounders — the roster of the first 100 seats. Live cap progress,
 * per-founder status (Premium active / lapsed), searchable list.
 */
export default function AdminFounders() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetchAdminFounders();
      setData(r);
    } catch (e) {
      console.error(e);
      toast.error("Could not load the founders roster.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const rows = data?.founders || [];
    if (!q.trim()) return rows;
    const needle = q.trim().toLowerCase();
    return rows.filter((r) =>
      `${r.name || ""} ${r.email || ""} #${r.number}`.toLowerCase().includes(needle)
    );
  }, [data, q]);

  const activeCount = useMemo(
    () => (data?.founders || []).filter((r) => r.subscription_active).length,
    [data]
  );

  return (
    <div className="min-h-screen">
      <header className="px-6 md:px-12 py-6 flex items-center justify-between border-b border-obsidian-600">
        <Link
          to="/admin"
          className="inline-flex items-center gap-2 font-sans text-xs uppercase tracking-[0.24em] text-bone-400 hover:text-bone-200 transition-colors"
          data-testid="back-to-admin"
        >
          <ChevronLeft className="h-3 w-3" /> Admin
        </Link>
        <div className="flex items-center gap-4">
          <AdminToggle />
          <UserBadge />
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-6 md:px-12 pt-10 sm:pt-16 pb-24">
        <p className="font-sans uppercase tracking-[0.24em] sm:tracking-[0.28em] text-xs text-terracotta mb-3 sm:mb-4">
          Umbra · Founders
        </p>
        <h1 className="font-serif text-3xl sm:text-4xl md:text-5xl text-bone-100 leading-tight mb-4">
          The first {data?.cap || 100} names on the wall.
        </h1>
        <p className="font-serif italic text-base sm:text-lg text-bone-400 max-w-2xl leading-relaxed mb-10">
          Founding members hold their seat for life. Watch the wall fill.
        </p>

        {loading ? (
          <div className="flex items-center gap-3 p-8 border border-obsidian-600">
            <Loader2 className="h-4 w-4 animate-spin text-terracotta" />
            <p className="font-sans text-bone-400">Gathering the roster…</p>
          </div>
        ) : (
          <>
            {/* Cap tile */}
            <div
              data-testid="founders-cap"
              className="border border-terracotta/40 bg-obsidian-800 p-5 sm:p-6 mb-8"
            >
              <div className="flex items-end justify-between mb-4 flex-wrap gap-4">
                <div className="flex items-center gap-3">
                  <Users className="h-5 w-5 text-terracotta" />
                  <div>
                    <p className="font-sans uppercase tracking-[0.24em] text-[0.65rem] text-bone-500 mb-1">Seats claimed</p>
                    <p className="font-serif text-3xl text-bone-100 leading-tight">
                      {data.total}{" "}
                      <span className="text-bone-400 text-lg font-sans uppercase tracking-[0.2em]">of {data.cap}</span>
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="font-sans uppercase tracking-[0.22em] text-[0.6rem] text-bone-500 mb-1">Active</p>
                  <p className="font-serif text-2xl text-terracotta" data-testid="founders-active">
                    {activeCount}
                  </p>
                </div>
              </div>
              <div className="h-2 bg-obsidian-900 relative overflow-hidden">
                <div
                  className="absolute inset-y-0 left-0 bg-terracotta transition-all"
                  style={{ width: `${(data.total / data.cap) * 100}%` }}
                />
              </div>
              <p className="font-sans text-[0.65rem] uppercase tracking-[0.24em] text-bone-500 mt-3">
                {data.remaining} seats remaining{data.closed && " · the wall is closed"}
              </p>
            </div>

            {/* Search */}
            <div className="relative max-w-md mb-6">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-bone-500 pointer-events-none" />
              <Input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search name, email, or number"
                data-testid="founders-search"
                className="rounded-none bg-obsidian-900 border-obsidian-600 focus-visible:ring-1 focus-visible:ring-terracotta text-bone-100 font-sans text-sm pl-10 pr-10 min-h-[44px]"
              />
              {q && (
                <button
                  onClick={() => setQ("")}
                  aria-label="Clear"
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-bone-500 hover:text-terracotta p-2 min-h-[44px] min-w-[44px] flex items-center justify-center"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </div>

            <p className="font-sans text-[0.65rem] uppercase tracking-[0.24em] text-bone-500 mb-4">
              Showing <span className="text-bone-200">{filtered.length}</span> of {data.total}
            </p>

            {/* Table */}
            {filtered.length === 0 ? (
              <div className="border border-obsidian-600 p-10 text-center">
                <p className="font-serif italic text-bone-300">No founders match this search.</p>
              </div>
            ) : (
              <div className="border border-obsidian-600 overflow-x-auto" data-testid="founders-table">
                <table className="w-full min-w-[640px] text-left font-sans text-sm">
                  <thead>
                    <tr className="border-b border-obsidian-600 bg-obsidian-800/50">
                      <th className="p-4 font-sans uppercase tracking-[0.2em] text-[0.6rem] text-bone-500">#</th>
                      <th className="p-4 font-sans uppercase tracking-[0.2em] text-[0.6rem] text-bone-500">Name</th>
                      <th className="p-4 font-sans uppercase tracking-[0.2em] text-[0.6rem] text-bone-500">Email</th>
                      <th className="p-4 font-sans uppercase tracking-[0.2em] text-[0.6rem] text-bone-500">Joined</th>
                      <th className="p-4 font-sans uppercase tracking-[0.2em] text-[0.6rem] text-bone-500">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((r) => (
                      <tr
                        key={r.user_id}
                        data-testid={`founder-row-${r.number}`}
                        className="border-b border-obsidian-600 last:border-0 hover:bg-obsidian-800/40 transition-colors"
                      >
                        <td className="p-4 text-terracotta font-serif text-lg">#{r.number}</td>
                        <td className="p-4 text-bone-200 font-serif">
                          {r.name || <span className="text-bone-500 italic">—</span>}
                        </td>
                        <td className="p-4 text-bone-300 font-mono text-xs sm:text-sm break-all">
                          {r.email || <span className="text-bone-500 italic">—</span>}
                        </td>
                        <td className="p-4 text-bone-400 whitespace-nowrap">
                          {r.joined_at
                            ? new Date(r.joined_at).toLocaleDateString(undefined, {
                                month: "short",
                                day: "numeric",
                                year: "numeric",
                              })
                            : <span className="text-bone-500 italic">—</span>}
                        </td>
                        <td className="p-4">
                          <span
                            data-testid={`founder-status-${r.number}`}
                            className={`inline-flex items-center font-sans uppercase tracking-[0.2em] text-[0.55rem] px-2 py-1 border ${
                              r.subscription_active
                                ? "border-terracotta/60 text-terracotta bg-terracotta/10"
                                : "border-obsidian-600 text-bone-500"
                            }`}
                          >
                            {r.subscription_active ? "Active" : "Lapsed"}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
